import { computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useSubscriptionStore } from '@/stores/subscriptionAccess'
import { PERMISSION_DENIED_MESSAGES } from '@/const/permissionDeniedCopy'
import { redirectToTuitionPortal } from '@/utils/tuitionError'

export function usePermissionDeniedView() {
  const router = useRouter()
  const userStore = useUserStore()
  const subscriptionStore = useSubscriptionStore()
  const { hasAccess, platformAccessDenied } = storeToRefs(subscriptionStore)

  const isRoleChanged = ref(false)
  const accessSyncPending = ref(true)

  const isTuitionAccessDenied = computed(() => !hasAccess.value)
  const isPlatformDenied = computed(() => platformAccessDenied.value)
  const showPaywallUi = computed(
    () => isTuitionAccessDenied.value || isPlatformDenied.value
  )

  const pageTitle = computed(() => {
    if (isRoleChanged.value) return PERMISSION_DENIED_MESSAGES.roleChanged.title
    if (isPlatformDenied.value)
      return PERMISSION_DENIED_MESSAGES.platformDenied.title
    if (isTuitionAccessDenied.value)
      return PERMISSION_DENIED_MESSAGES.tuitionDenied.title
    return PERMISSION_DENIED_MESSAGES.rolePending.title
  })

  const message = computed(() => {
    if (isPlatformDenied.value) {
      return PERMISSION_DENIED_MESSAGES.platformDenied.message
    }
    if (isTuitionAccessDenied.value) {
      return (
        subscriptionStore.message ||
        PERMISSION_DENIED_MESSAGES.tuitionDenied.fallbackMessage
      )
    }
    return PERMISSION_DENIED_MESSAGES.rolePending.message
  })

  const buttonText = computed(() => {
    if (isRoleChanged.value)
      return PERMISSION_DENIED_MESSAGES.roleChanged.primaryAction
    if (isPlatformDenied.value)
      return PERMISSION_DENIED_MESSAGES.platformDenied.primaryAction
    if (isTuitionAccessDenied.value) {
      return (
        subscriptionStore.actionButtonText ||
        PERMISSION_DENIED_MESSAGES.tuitionDenied.fallbackAction
      )
    }
    return PERMISSION_DENIED_MESSAGES.logout.primaryAction
  })

  const paywallPath = computed(() => {
    if (isPlatformDenied.value) {
      return PERMISSION_DENIED_MESSAGES.platformDenied.actionPath
    }
    return subscriptionStore.actionUrl || undefined
  })

  const handleAction = async () => {
    if (showPaywallUi.value) {
      redirectToTuitionPortal(paywallPath.value)
    } else {
      await userStore.logout()
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      router.push('/login')
    }
  }

  const checkUserRoleAndRedirect = async () => {
    try {
      const roleCheck = await userStore.checkUserRole()
      const currentRole = (roleCheck.current_role || '').toUpperCase()

      if (roleCheck.role_changed) {
        isRoleChanged.value = true
        await userStore.logout()
        return
      }

      if (currentRole === 'GUIDE') {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        router.push('/applications/all')
      } else if (['FELLOW', 'MENTOR', 'COMMON'].includes(currentRole)) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        router.push('/jobs')
      }
    } catch {
      if (hasAccess.value) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        router.push('/')
      }
    }
  }

  onMounted(async () => {
    try {
      await subscriptionStore.checkStatus({
        preserveStateOnNetworkError: true
      })
      if (hasAccess.value && !platformAccessDenied.value) {
        await checkUserRoleAndRedirect()
      }
    } finally {
      accessSyncPending.value = false
    }
  })

  const handleLogout = async () => {
    await userStore.logout()
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.push('/login')
  }

  return {
    accessSyncPending,
    pageTitle,
    message,
    buttonText,
    handleAction,
    handleLogout
  }
}
